import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, map, take } from 'rxjs';
import { AuthFacade } from './auth-facade';

/**
 * Issuer-aware replacement for the Auth0 `AuthGuard` on app-shell routes
 * (#29713). The Auth0 guard only recognizes Auth0 sessions, so a successful
 * Entra sign-in was rejected at the shell and bounced back to the login
 * page forever. This guard asks the {@link AuthFacade} instead, which
 * reports the ACTIVE issuer's session state (MSAL when the session rides
 * Entra, Auth0 otherwise — both implementations only emit after their SDK
 * finishes initializing, so `take(1)` cannot read a premature false).
 *
 * Rejections route to the app's own identifier-first `/login` page rather
 * than dispatching a third-party redirect: the login page owns realm
 * discovery (email → Entra or Auth0, phone → Auth0), so it must be the
 * single unauthenticated entry point.
 */
export const issuerAwareAuthGuard: CanActivateFn = (): Observable<
  boolean | UrlTree
> => {
  const facade = inject(AuthFacade);
  const router = inject(Router);
  return facade.isAuthenticated$.pipe(
    take(1),
    map((authenticated) =>
      authenticated
        ? true
        : router.createUrlTree(['/login'], {
            queryParams: { returnTo: router.url },
          })
    )
  );
};
